import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AiImagePolicy,
  AuthUser,
  CreateBrandInput,
  LoginInput,
  PlanKey,
  RegisterInput,
  UpdateBrandInput,
  WorkspaceRole,
  Plan,
} from "@marketing/shared";
import { api, setTokens, unwrap } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";

export type Brand = {
  id: string;
  name: string;
  logoUrl: string | null;
  industry: string | null;
  country: string;
  timezone: string;
  cart10StoreId: string | null;
  aiImagePolicy: AiImagePolicy;
  createdAt: string;
  _count?: { socialAccounts: number; posts: number; voices: number; approvers: number };
  /** Present once the brand's fact sheet has been saved at least once. */
  profile?: { id: string } | null;
};

export type CurrentWorkspace = {
  id: string;
  name: string;
  plan: PlanKey;
  billingInterval: string;
  country: string;
  timezone: string;
  role: WorkspaceRole;
  limits: Plan;
};

export function useMe() {
  const setUser = useAuthStore((s) => s.setUser);
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const user = await unwrap<AuthUser>(api.get("/auth/me"));
      setUser(user);
      return user;
    },
    retry: false,
    staleTime: 60_000,
  });
}

export function useLogin() {
  return useMutation({
    mutationFn: async (input: LoginInput) => {
      const data = await unwrap<{ accessToken: string; refreshToken: string }>(
        api.post("/auth/login", input),
      );
      setTokens(data);
      return data;
    },
  });
}

export function useRegister() {
  return useMutation({
    mutationFn: async (input: RegisterInput) => {
      const data = await unwrap<{ accessToken: string; refreshToken: string }>(
        api.post("/auth/register", input),
      );
      setTokens(data);
      return data;
    },
  });
}

/** Scoped by the X-Workspace-Id header, so the key includes it. */
export function useBrands() {
  const workspaceId = useAuthStore((s) => s.workspaceId);
  return useQuery({
    queryKey: ["brands", workspaceId],
    queryFn: () => unwrap<Brand[]>(api.get("/brands")),
    enabled: !!workspaceId,
  });
}

export function useCurrentWorkspace() {
  const workspaceId = useAuthStore((s) => s.workspaceId);
  return useQuery({
    queryKey: ["workspace", "current", workspaceId],
    queryFn: () => unwrap<CurrentWorkspace>(api.get("/workspaces/current")),
    enabled: !!workspaceId,
  });
}

export function useUpdateBrand() {
  const qc = useQueryClient();
  const workspaceId = useAuthStore((s) => s.workspaceId);
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateBrandInput }) =>
      unwrap<Brand>(api.patch(`/brands/${id}`, input)),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["brands", workspaceId] }),
  });
}

export function useCreateBrand() {
  const qc = useQueryClient();
  const workspaceId = useAuthStore((s) => s.workspaceId);
  return useMutation({
    mutationFn: (input: CreateBrandInput) => unwrap<Brand>(api.post("/brands", input)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["brands", workspaceId] });
      void qc.invalidateQueries({ queryKey: ["workspace", "current", workspaceId] });
    },
  });
}
